import { apiClient } from './axios';

import type {
  CreateWorkflowEntityDto,
  PostCallWorkflow,
  UpdateWorkflowEntityDto,
  WorkflowEntity,
} from '@repo/shared-types';

/** One node's result inside a persisted execution log. */
export interface ExecutionNodeResult {
  nodeId: string;
  nodeType: string;
  status: 'success' | 'error' | 'skipped';
  branch?: string;
  message?: string;
  duration_ms?: number;
  request_payload?: unknown;
  response_data?: unknown;
}

export interface ExecutionLogBody {
  started_at?: string;
  completed_at?: string;
  total_nodes?: number;
  executed_nodes?: number;
  errors?: number;
  dry_run?: boolean;
  results?: ExecutionNodeResult[];
  returnValue?: unknown;
  /** What triggered the run — the webhook body, headers and query. */
  trigger_payload?: {
    body?: unknown;
    headers?: Record<string, string>;
    query?: Record<string, string>;
    method?: string;
    ip?: string;
    received_at?: string;
  };
}

/** Live click state for a link the run generated, read at request time. */
export interface ExecutionTrackedLink {
  id: string;
  trackingId: string;
  linkType: string;
  originalUrl: string;
  clicked: boolean;
  clickedAt: string | null;
  createdAt: string;
}

export interface WorkflowExecutionLogDetail {
  id: string;
  workflow_id: string;
  workflow_name: string;
  trigger_type: string;
  source_id: string | null;
  status: string;
  total_nodes: number;
  executed_nodes: number;
  errors: number;
  execution_log: ExecutionLogBody;
  created_at: string;
  workflow: {
    id: string;
    name: string;
    description: string | null;
    trigger_type: string;
    post_call_workflow: PostCallWorkflow | null;
  } | null;
  tracked_links: ExecutionTrackedLink[];
}

export const workflowsApi = {
  getAll: async () => {
    const response = await apiClient.get<WorkflowEntity[]>('/workflows');
    return response.data;
  },

  getOne: async (id: string) => {
    const response = await apiClient.get<WorkflowEntity>(`/workflows/${id}`);
    return response.data;
  },

  create: async (data: CreateWorkflowEntityDto) => {
    const response = await apiClient.post<WorkflowEntity>('/workflows', data);
    return response.data;
  },

  update: async (id: string, data: UpdateWorkflowEntityDto) => {
    const response = await apiClient.patch<WorkflowEntity>(`/workflows/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    await apiClient.delete(`/workflows/${id}`);
  },

  executeForCall: async (callId: string, options?: { workflowId?: string; dryRun?: boolean }) => {
    const response = await apiClient.post(`/workflows/execute/${callId}`, options ?? {});
    return response.data;
  },

  runWorkflow: async (id: string) => {
    const response = await apiClient.post(`/workflows/${id}/run`);
    return response.data;
  },

  generateWebhookToken: async (id: string) => {
    const response = await apiClient.post<{ token: string; webhook_url: string }>(
      `/workflows/${id}/webhook-token`,
    );
    return response.data;
  },

  getWebhookTestPayload: async (id: string) => {
    const response = await apiClient.get<{
      test_payload: {
        captured_at: string;
        sample: Record<string, unknown>;
        schema: Record<string, unknown>;
      } | null;
    }>(`/workflows/${id}/webhook-test`);
    return response.data;
  },

  getExecutionLogs: async (params: { page?: number; limit?: number; workflowId?: string }) => {
    const response = await apiClient.get('/workflows/execution-logs', { params });
    return response.data as {
      data: Array<{
        id: string;
        workflow_id: string;
        workflow_name: string;
        trigger_type: string;
        source_id: string | null;
        status: string;
        total_nodes: number;
        executed_nodes: number;
        errors: number;
        execution_log: Record<string, unknown>;
        created_at: string;
      }>;
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  },

  getExecutionLog: async (id: string) => {
    const response = await apiClient.get(`/workflows/execution-logs/${id}`);
    return response.data as WorkflowExecutionLogDetail;
  },
};
